/**
 * Feasibility from a file in the DANI chat (UAT, 2026-10-15). A brief, an
 * email, a questionnaire or a location or panelist list goes through the same
 * reader as the Feasibility page (`POST /feasibility/brief`): it returns one
 * draft request per study, with the location lists and panelist IDs already
 * attached. DANI is told what each draft says but never handed the lists; it
 * runs one by naming it (`"fromFile": "<key>"` in a feasibility-check study)
 * with any field it changed, and the card fills in the rest from here.
 *
 * The drafts are kept in this browser for the conversation, like a card's slot.
 */
import type { BriefDraft, BriefResponse } from '@/lib/feasibility/contracts/brief';
import { describeRequestCriteria } from '@/lib/feasibility/engine/project';
import {
    FeasibilityRequestSchema,
    type FeasibilityRequest,
} from '@/lib/feasibility/engine/request';
import { LAUNCH_HEADER } from '@/lib/feasibility/dani/primer-marks';

const KEY = 'fz:file:';

export interface FileDrafts {
    briefId: string;
    files: string[];
    drafts: BriefDraft[];
}

export function writeFileDrafts(conversationId: string, value: FileDrafts): void {
    try {
        const all = readAll(conversationId);
        all.push(value);
        window.localStorage.setItem(KEY + conversationId, JSON.stringify(all.slice(-5)));
    } catch {
        /* storage unavailable: DANI can still describe the study, the card asks again */
    }
}

function readAll(conversationId: string): FileDrafts[] {
    try {
        const raw = window.localStorage.getItem(KEY + conversationId);
        return raw ? (JSON.parse(raw) as FileDrafts[]) : [];
    } catch {
        return [];
    }
}

/** The draft a study names, newest file first; null when this browser does not have it. */
export function findFileDraft(conversationId: string | null, key: string): BriefDraft | null {
    if (!conversationId) return null;
    for (const f of readAll(conversationId).reverse()) {
        const d = f.drafts.find((x) => x.key === key);
        if (d) return d;
    }
    return null;
}

/** The fields DANI may change on a draft from a file; everything else comes from the file. */
const CHANGEABLE = [
    'title',
    'target',
    'incidenceRate',
    'fieldDays',
    'programmingType',
    'loiMinutes',
    'phases',
    'daysBetweenPhases',
    'plannedSends',
] as const;

/** A draft with DANI's changes, checked against the request contract. */
export function applyFileStudy(draft: BriefDraft, study: Record<string, unknown>): BriefDraft {
    const request: Record<string, unknown> = { ...draft.request };
    for (const k of CHANGEABLE) if (study[k] != null) request[k] = study[k];
    if (typeof study.incentiveDollars === 'number' && study.incentiveDollars >= 0)
        request.incentiveCents = Math.round(study.incentiveDollars * 100);
    // The control group's completes ("150 test / 150 control"), said in the chat, not the file.
    const control =
        typeof study.controlTarget === 'number' ? Math.round(study.controlTarget) : null;
    if (control != null && control >= 1 && Array.isArray(request.criteria))
        request.criteria = (request.criteria as Record<string, unknown>[]).map((c) =>
            c.kind === 'ooh_exposure' && c.control && typeof c.control === 'object'
                ? { ...c, control: { ...(c.control as Record<string, unknown>), target: control } }
                : c,
        );
    const parsed: FeasibilityRequest = FeasibilityRequestSchema.parse(request);
    return {
        ...draft,
        title: typeof study.title === 'string' ? study.title : draft.title,
        request: parsed,
        // A share who qualify DANI gives settles the one the file did not have.
        incidenceUnconfirmed: study.incidenceRate != null ? false : draft.incidenceUnconfirmed,
    };
}

/** A draft's control list, with its completes when set. */
function controlListOf(r: FeasibilityRequest): { target: number | null } | null {
    const c = r.criteria.find((x) => x.kind === 'ooh_exposure');
    return c && c.kind === 'ooh_exposure' && c.control
        ? { target: c.control.target ?? null }
        : null;
}

const pct = (n: number) => `${Math.round(n * 1000) / 10}%`;

/** What Studio tells DANI after reading a file: each draft, where its fields came from, and how to run it. */
export function fileBriefForDani(
    read: BriefResponse,
    files: string[],
    rules: string | null,
): string {
    const lines = [
        LAUNCH_HEADER,
        `The person attached ${files.join(', ')} for a feasibility check. Studio read it into ${read.drafts.length} draft request${read.drafts.length === 1 ? '' : 's'}${read.locations ? `, with ${read.locations}` : ''}${read.panelists ? `, and ${read.panelists}` : ''}.`,
        '',
    ];
    for (const d of read.drafts) {
        const r = d.request;
        lines.push(
            `DRAFT "${d.key}": ${d.title}`,
            `- Study type: ${r.studyType}; completes: ${r.target ?? 'not given'}; days in field: ${r.fieldDays}; share who qualify: ${r.incidenceRate != null ? pct(r.incidenceRate) : 'not given'}${d.incidenceUnconfirmed ? ' (a default, not from the file: confirm it)' : ''}; built ${r.programmingType === 'external' ? 'behind a link' : 'in Studio'}${r.loiMinutes ? `; ${r.loiMinutes} minutes` : ''}`,
            `- Audience: ${describeRequestCriteria(r).join('; ') || 'everyone in the panel'}`,
            ...Object.entries(d.sources)
                .slice(0, 8)
                .map(
                    ([field, s]) =>
                        `- ${field} ${s.kind === 'brief' ? 'from the file' : 'suggested'}: "${s.passage.slice(0, 140)}"`,
                ),
            ...(controlListOf(r)
                ? [
                      controlListOf(r)!.target != null
                          ? `- Test and control: ${controlListOf(r)!.target} completes in the control group, the rest in the test group.`
                          : '- Test and control: the file has a control list but not the split. If the person said it ("150 test / 150 control"), pass controlTarget with the control group\'s completes; otherwise ask for it after the number.',
                  ]
                : []),
            ...(d.questions.length ? [`- The file does not say: ${d.questions.join(' ')}`] : []),
            ...(d.cannotTarget.length
                ? [`- Cannot be targeted: ${d.cannotTarget.join('; ')}`]
                : []),
            '',
        );
    }
    if (read.ignoredInstructions.length)
        lines.push('The file had text addressed to the tool; it was ignored.', '');
    lines.push(
        'Tell the person in one or two sentences what you read (the study and the audience, and the lists if any), and run it in this same reply: write the feasibility-check block with studies like [{"fromFile": "<draft key>"}], adding only the fields you or the person changed (title, target, incidenceRate, fieldDays, programmingType, loiMinutes, phases, plannedSends, incentiveDollars, controlTarget). controlTarget is the completes in the control group of a test-and-control study; target stays the two groups together. Never retype the locations, lists or audience: the draft carries them. Do not ask first: Studio runs it on its assumptions and sends you what to ask after the number.',
        ...(rules ? ['', rules] : []),
    );
    return lines.join('\n');
}
